import React, { useState, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Upload, X, Image as ImageIcon, AlertCircle } from 'lucide-react';

const MediaUploader = ({ 
  onUpload, 
  currentImage = null, 
  onRemove,
  maxSize = 5 * 1024 * 1024, // 5MB default
  acceptedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  className = ""
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const functions = getFunctions();
  const getSignedUploadUrl = httpsCallable(functions, 'getSignedUploadUrl');

  const validateFile = (file) => {
    const errors = [];
    
    if (file.size > maxSize) {
      errors.push(`File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB`);
    }
    
    if (!acceptedTypes.includes(file.type)) {
      errors.push('Only JPEG, JPG, PNG, and WebP images are allowed');
    }
    
    return errors;
  };

  const handleFileUpload = async (file) => {
    setError(null);
    setUploadProgress(0);
    
    // Validate file
    const validationErrors = validateFile(file);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setUploading(true);
    
    try {
      // Get signed upload URL from Cloud Function
      console.log('Requesting signed upload URL...');
      const result = await getSignedUploadUrl({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      });

      const { uploadUrl, fileName, filePath } = result.data;
      console.log('Got signed URL, uploading file...');

      // Upload to Cloud Storage using signed URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 
          'Content-Type': file.type 
        }
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      console.log('File uploaded successfully');
      
      // Generate the public URLs for the processed images
      // Note: In a real implementation, you might want to wait for processing
      // or implement a callback system
      const baseUrl = `https://storage.googleapis.com/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}`;
      const processedPath = filePath.replace('blog-images/', 'blog-images/processed/');
      
      const imageUrls = {
        original: `${baseUrl}/${filePath}`,
        thumbnail: `${baseUrl}/${processedPath.replace(/\.(jpg|jpeg|png|webp)$/i, '_thumbnail.jpg')}`,
        medium: `${baseUrl}/${processedPath.replace(/\.(jpg|jpeg|png|webp)$/i, '_medium.jpg')}`,
        large: `${baseUrl}/${processedPath.replace(/\.(jpg|jpeg|png|webp)$/i, '_large.jpg')}`,
        webp: `${baseUrl}/${processedPath.replace(/\.(jpg|jpeg|png|webp)$/i, '.webp')}`
      };

      if (onUpload) {
        onUpload(imageUrls);
      }
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleRemoveImage = () => {
    if (onRemove) {
      onRemove();
    }
    setError(null);
  };

  return (
    <div className={`media-uploader ${className}`}>
      {currentImage ? (
        <div className="current-image-container">
          <div className="relative inline-block">
            <img 
              src={currentImage.thumbnail || currentImage.medium || currentImage.original} 
              alt="Featured" 
              className="w-48 h-32 object-cover rounded-lg shadow-md"
            />
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
              disabled={uploading}
            >
              <X size={16} />
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            Click to replace image
          </div>
        </div>
      ) : null}
      
      <div 
        className={`upload-dropzone border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver ? 'border-purple-400 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
        } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-3"></div>
            <p className="text-gray-600">Uploading... {uploadProgress}%</p>
          </div>
        ) : (
          <div className="flex flex-col items-center cursor-pointer">
            <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg mb-3">
              {currentImage ? <ImageIcon size={24} className="text-gray-500" /> : <Upload size={24} className="text-gray-500" />}
            </div>
            <p className="text-gray-700 font-medium mb-1">
              {currentImage ? 'Replace Image' : 'Upload Image'}
            </p>
            <p className="text-gray-500 text-sm mb-2">
              Drag and drop or click to select
            </p>
            <p className="text-xs text-gray-400">
              Max {Math.round(maxSize / 1024 / 1024)}MB • JPG, PNG, WebP
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle size={16} className="text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />
    </div>
  );
};

export default MediaUploader;
